"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import {
  Database,
  Activity,
  Shield,
  ShieldAlert,
  Layers,
  LineChart,
  ScanSearch,
  Wrench,
  Trash2,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
} from "lucide-react";

type NavLeaf = { name: string; href: string; icon: LucideIcon };
type NavGroup = { name: string; icon: LucideIcon; children: NavLeaf[] };
type NavItem = NavLeaf | NavGroup;

const isGroup = (item: NavItem): item is NavGroup => "children" in item;

const navigation: NavItem[] = [
  { name: "Resources", href: "/resources", icon: Database },
  { name: "Dashboard", href: "/", icon: Activity },
  { name: "Access Control", href: "/policies", icon: Shield },
  { name: "Access Guard Violations", href: "/access-guard", icon: ShieldAlert },
  { name: "Metrics", href: "/metrics", icon: LineChart },
  { name: "Interference Analysis", href: "/interference-analysis", icon: ScanSearch },
  {
    name: "Technical APIs",
    icon: Wrench,
    children: [
      { name: "Deleted Resource", href: "/technical", icon: Trash2 },
    ],
  },
];

function leafClasses(isActive: boolean, collapsed: boolean) {
  return cn(
    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
    collapsed && "justify-center gap-0 px-2",
    isActive
      ? "bg-primary text-primary-foreground"
      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
  );
}

function NavGroupItem({
  group,
  pathname,
  collapsed,
}: {
  group: NavGroup;
  pathname: string;
  collapsed: boolean;
}) {
  const hasActiveChild = group.children.some((c) => c.href === pathname);
  // Expand the group by default when one of its children is the active page,
  // so a deep link lands with its parent open.
  const [open, setOpen] = useState(hasActiveChild);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={collapsed ? group.name : undefined}
        title={collapsed ? group.name : undefined}
        className={cn(
          "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          collapsed && "justify-center gap-0 px-2",
          "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
        )}
      >
        <group.icon aria-hidden="true" className="h-5 w-5 shrink-0" />
        {!collapsed && (
          <>
            <span className="flex-1 text-left">{group.name}</span>
            <ChevronRight
              aria-hidden="true"
              className={cn(
                "h-4 w-4 transition-transform",
                open && "rotate-90"
              )}
            />
          </>
        )}
      </button>
      {open && (
        <div className={cn("mt-1 space-y-1 pl-4", collapsed && "pl-0")}>
          {group.children.map((child) => (
            <Link
              key={child.name}
              href={child.href}
              aria-label={collapsed ? child.name : undefined}
              title={collapsed ? child.name : undefined}
              className={leafClasses(pathname === child.href, collapsed)}
            >
              <child.icon aria-hidden="true" className="h-5 w-5 shrink-0" />
              {!collapsed && child.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar() {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div
      data-testid="sidebar"
      className={cn(
        "flex h-screen shrink-0 flex-col overflow-hidden border-r bg-card transition-[width] duration-200",
        collapsed ? "w-16" : "w-64"
      )}
    >
      <div
        className={cn(
          "flex h-16 shrink-0 items-center border-b",
          collapsed ? "justify-center px-2" : "gap-3 px-4"
        )}
      >
        {!collapsed && (
          <>
            <Layers aria-hidden="true" className="h-6 w-6 shrink-0 text-primary" />
            <h1 className="min-w-0 flex-1 truncate text-xl font-bold text-primary">
              URM Admin
            </h1>
          </>
        )}
        <button
          type="button"
          onClick={() => setCollapsed((value) => !value)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {collapsed ? (
            <PanelLeftOpen aria-hidden="true" className="h-5 w-5" />
          ) : (
            <PanelLeftClose aria-hidden="true" className="h-5 w-5" />
          )}
        </button>
      </div>
      <nav className={cn("flex-1 space-y-1", collapsed ? "p-2" : "p-4")}>
        {navigation.map((item) =>
          isGroup(item) ? (
            <NavGroupItem
              key={item.name}
              group={item}
              pathname={pathname}
              collapsed={collapsed}
            />
          ) : (
            <Link
              key={item.name}
              href={item.href}
              aria-label={collapsed ? item.name : undefined}
              title={collapsed ? item.name : undefined}
              className={leafClasses(pathname === item.href, collapsed)}
            >
              <item.icon aria-hidden="true" className="h-5 w-5 shrink-0" />
              {!collapsed && item.name}
            </Link>
          )
        )}
      </nav>
      <div className={cn("border-t", collapsed ? "p-2" : "p-4")}>
        <div className={cn("rounded-lg bg-muted", collapsed ? "p-2" : "p-3")}>
          {collapsed ? (
            <p
              className="text-center text-xs font-semibold text-muted-foreground"
              title="Unified Resource Manager v1.0.0"
            >
              URM
            </p>
          ) : (
            <>
              <p className="text-xs font-medium text-muted-foreground">
                Unified Resource Manager
              </p>
              <p className="mt-1 text-xs text-muted-foreground">v1.0.0</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
